/**
 * Sink for finalised transcript segments.
 *
 * Owns everything that happens AFTER a channel emits a final
 * transcript:
 *
 *   - Push the raw text to the in-memory `textSegments` ring (used
 *     by `getTextForRange` for PTT context).
 *   - Clear the partial line on the partials broadcast topic and report
 *     segment completion to PTT in main.
 *   - Build the dual-shape "processed / provider" transcript entry
 *     and hand it to the main process via the targeted
 *     `transcripts:entry` invoke (never broadcast to other renderers).
 *   - Resolve any in-flight `forceSegment` awaiters for the source.
 *
 * Pulled out of `AudioTranscriptionService` so the orchestrator
 * doesn't have to know any of the GlobalState payload shapes.
 */
import { GlobalEvents, GlobalState } from '@highlight/ipc'
import * as Sentry from '@sentry/electron/renderer'
import type { TranscriptEntry } from '@highlight/common'
import type { AudioNoteState } from '@shared/types/AudioNoteState'
import type { SpeakerNameResolver } from '../speaker-name-resolver'
import { TRANSCRIPT_ENTRY_EVENT } from '@shared/transcript-feed'
import { publishPartialTranscription } from './partials-publisher'
import { reportSegmentEndToMain } from './segment-end'
import { isDev } from '@shared/utils/platform'
import { createTranscriptEntry } from '@shared/utils/audio-notes/create-transcript-entry'
import { neutralMicLabel } from '@shared/utils/audio-notes/mic-label'
import { SOURCE_LOOPBACK, SOURCE_MICROPHONE, type ChannelLabel, type TextSegment } from './types'

export interface TranscriptDispatcherDelegate {
  /** Called when a transcript should resolve any in-flight
   *  `forceSegment` awaiters for that source. */
  onForceSegmentResolved(source: number): void
}

export class TranscriptDispatcher {
  /** Append-only buffer of finalised segments. Read by the service's
   *  `getTextForRange` for PTT context. Cleared by `reset()`. */
  private textSegments: Array<TextSegment> = []

  constructor(
    private readonly speakerResolverProvider: () => SpeakerNameResolver,
    private readonly delegate: TranscriptDispatcherDelegate,
  ) {}

  /** Reset the in-memory transcript buffer. Called on `start()` and
   *  `flushDetectionState()` so a new session doesn't pick up the prior
   *  session's transcripts. */
  reset(): void {
    this.textSegments = []
  }

  /** Read concatenated text for a source in the given absolute time
   *  window. PTT uses this to feed recent dictation as system context. */
  getTextForRange(source: number, startTime: number, endTime: number): string {
    return this.textSegments
      .filter((s) => s.source === source && s.endTime >= startTime && s.startTime <= endTime)
      .map((s) => s.text)
      .join(' ')
  }

  /** Insert a `[~Ns partially not transcribed — connection
   *  interrupted, buffer full]` marker at the current wall-clock
   *  time. Triggered by the channel when a reconnect happens after
   *  the PCM buffer overflowed. */
  insertGapMarker(channel: ChannelLabel, gapSeconds: number): void {
    const now = Date.now()
    const noteState = GlobalState.get<AudioNoteState | undefined>('audio-note-state')
    const startMs = noteState?.startTime ? new Date(noteState.startTime).getTime() : now

    const entry = createTranscriptEntry({
      speaker: 'System',
      text: `[~${gapSeconds}s partially not transcribed — connection interrupted, buffer full]`,
      absoluteTimestamp: new Date(now).toISOString(),
      relativeTimestampMs: now - startMs,
      isHost: channel === 'mic',
    })

    this.sendTranscriptEntryToMain(entry, entry)
  }

  /** Hand a finalised entry pair to the main process. Fire-and-forget:
   *  a failed invoke (e.g. handler not yet installed during teardown)
   *  is reported, not thrown — nothing awaits transcript delivery. */
  private sendTranscriptEntryToMain(processed: TranscriptEntry, provider: TranscriptEntry): void {
    void GlobalEvents.invoke(TRANSCRIPT_ENTRY_EVENT, { processed, provider }).catch((err) => {
      // A dropped entry is a line missing from the saved note — surface it.
      console.error('[TranscriptDispatcher] Failed to deliver transcript entry to main:', err)
      Sentry.captureException(
        err instanceof Error ? err : new Error(`transcript entry invoke failed: ${String(err)}`),
        {
          tags: { module: 'transcript-dispatcher' },
        },
      )
    })
  }

  /** Handle a finalised transcript segment from an AudioChannel.
   *  Updates GlobalState, runs mic auto-resolution, and resolves any
   *  pending forceSegment awaiters via the delegate. */
  handleTranscript(seg: TextSegment): void {
    const source = seg.source
    const isHost = !!seg.isHost
    const now = Date.now()

    this.textSegments.push(seg)

    if (source === SOURCE_MICROPHONE) {
      publishPartialTranscription('microphone', null)
      reportSegmentEndToMain(source, now, {
        text: seg.text,
        startTime: seg.startTime,
        endTime: seg.endTime,
      })
    } else if (source === SOURCE_LOOPBACK) {
      publishPartialTranscription('loopback', null)
      reportSegmentEndToMain(source, now)
    }

    if (isDev) {
      console.log(
        `[AudioTranscriptionService:${isHost ? 'mic' : 'sys'}] transcript: speaker=${seg.speaker} text="${seg.text.slice(0, 80)}"`,
      )
    }

    this.publishTranscriptEntry(seg)
    this.delegate.onForceSegmentResolved(source)
  }

  /** Build and publish the dual-shape transcript entry. The processed
   *  entry uses the resolved speaker (auto-named for mic, resolver-
   *  resolved for system); the provider entry uses raw labels so
   *  downstream consumers can tell mic-vs-system apart and apply
   *  their own resolution. */
  private publishTranscriptEntry(seg: TextSegment): void {
    if (!seg.text.trim()) return

    const noteState = GlobalState.get<{ startTime?: string | number | Date } | undefined>('audio-note-state')
    const startMs = noteState?.startTime ? new Date(noteState.startTime).getTime() : seg.endTime

    const isHost = seg.isHost ?? seg.source === SOURCE_MICROPHONE
    const rawSpeaker = seg.speaker || (isHost ? 'You' : 'Other')
    const absoluteTimestamp = new Date(seg.endTime).toISOString()
    const relativeTimestampMs = seg.endTime - startMs
    const speakerDigitMatch = rawSpeaker.match(/(\d+)/)
    const bareSpeakerIndex = speakerDigitMatch ? speakerDigitMatch[1] : undefined
    const dgSpeakerIndex = bareSpeakerIndex != null ? `${isHost ? 'mic' : 'sys'}:${bareSpeakerIndex}` : undefined

    const speakerResolver = this.speakerResolverProvider()
    const resolvedSpeaker = isHost
      ? // Produce a neutral mic-speaker label that carries the
        // diarization letter but no owner name. The owner name is
        // resolved at render time from current user state — see
        // `resolveMicLabel` in `@shared/utils/audio-notes/mic-label`.
        // This avoids baking an empty/stale owner name into the
        // entry when the audio runtime renderer hasn't finished
        // syncing user state from main by the time the first
        // transcripts arrive.
        neutralMicLabel(bareSpeakerIndex)
      : speakerResolver.resolve(rawSpeaker)

    const entry = createTranscriptEntry({
      speaker: resolvedSpeaker,
      text: seg.text,
      absoluteTimestamp,
      relativeTimestampMs,
      isHost,
      dgSpeakerIndex,
      micEpoch: seg.micEpoch,
    })

    // Raw-Deepgram label: mic entries always use neutralMicLabel (so
    // the UI can detect them as mic via the "My mic — X" pattern and
    // resolveMicLabel can rewrite to "{Owner}'s mic — X" at render
    // time). System entries use a 1-indexed "Speaker N" label so
    // they're visually distinct from mic. When Deepgram omits a
    // speaker index (short utterances, no diarization), fall back to
    // "A"/rawSpeaker respectively — matching the processed path's
    // resolveMicSpeaker behaviour.
    const rawEntry = createTranscriptEntry({
      speaker: isHost
        ? neutralMicLabel(bareSpeakerIndex)
        : bareSpeakerIndex != null
          ? `Speaker ${parseInt(bareSpeakerIndex, 10) + 1}`
          : rawSpeaker,
      text: seg.text,
      absoluteTimestamp,
      relativeTimestampMs,
      isHost,
      dgSpeakerIndex,
      micEpoch: seg.micEpoch,
    })

    this.sendTranscriptEntryToMain(entry, rawEntry)
  }
}
